import React, { useEffect, useState } from "react";
import { ChevronsUpDown, FolderKanban, Building2, Command as CommandIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";

const BADGE_STYLE = {
  PRIVATE: "text-[--six-crimson] border-[--six-crimson]/40 bg-[--six-crimson-soft]",
  CLIENT: "text-[--tx-1] border-[--hairline] bg-[--shell-3]",
  DEMO: "text-[--tx-3] border-[--hairline] bg-[--shell-2]",
};

export const IsolationBadge = ({ badge, className = "" }) => (
  <span className={`inline-flex items-center rounded px-1.5 py-px text-[9px] font-mono font-semibold tracking-wider border ${BADGE_STYLE[badge] || BADGE_STYLE.CLIENT} ${className}`}>
    {badge}
  </span>
);

export const WorkspaceSwitcher = () => {
  const { workspaces, currentProject, currentWorkspace, switchProject } = useAuth();
  const [cmdOpen, setCmdOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!currentProject) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="hidden md:flex items-center gap-2 rounded-lg border border-[--hairline] bg-[--shell-3] px-2.5 h-8 max-w-[280px] hover:bg-[#243041] transition-colors"
            data-testid="workspace-switcher-trigger" title="Switch workspace / project (Cmd+K)">
            <FolderKanban size={13} className="text-[--tx-3] shrink-0" />
            <span className="text-xs font-semibold text-[--tx-1] truncate" data-testid="topbar-project-pill">
              {currentProject.name}
            </span>
            <IsolationBadge badge={currentWorkspace?.badge} />
            <ChevronsUpDown size={12} className="text-[--tx-3] shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[320px] bg-[--shell-1] border-[--hairline]" data-testid="workspace-switcher-menu">
          {workspaces.map((w, i) => (
            <React.Fragment key={w.id}>
              {i > 0 && <DropdownMenuSeparator className="bg-[--divider]" />}
              <DropdownMenuLabel className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[--tx-3]">
                <Building2 size={11} /> {w.name} <IsolationBadge badge={w.badge} className="ml-auto" />
              </DropdownMenuLabel>
              {(w.projects || []).map((p) => (
                <DropdownMenuItem key={p.id} onClick={() => p.id !== currentProject.id && switchProject(p.id)}
                  className={`text-xs cursor-pointer ${p.id === currentProject.id ? "bg-white/5 text-[--tx-1]" : "text-[--tx-2]"}`}
                  data-testid={`workspace-switcher-project-item-${p.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{p.name}</div>
                    <div className="text-[10px] text-[--tx-3] truncate">{p.industry} · {p.use_case}</div>
                  </div>
                  {p.id === currentProject.id && <span className="ml-auto text-[9px] font-mono text-[--ai]">ACTIVE</span>}
                </DropdownMenuItem>
              ))}
            </React.Fragment>
          ))}
          <DropdownMenuSeparator className="bg-[--divider]" />
          <div className="px-2 py-1.5 text-[10px] text-[--tx-3] flex items-center gap-1.5">
            <CommandIcon size={10} /> Cmd+K opens the quick switcher
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <CommandDialog open={cmdOpen} onOpenChange={setCmdOpen}>
        <CommandInput placeholder="Switch workspace or project..." data-testid="workspace-switcher-command-input" />
        <CommandList>
          <CommandEmpty>No matching projects.</CommandEmpty>
          {workspaces.map((w) => (
            <CommandGroup key={w.id} heading={`${w.name} — ${w.badge}`}>
              {(w.projects || []).map((p) => (
                <CommandItem key={p.id} value={`${w.name} ${p.name} ${p.industry}`}
                  onSelect={() => { setCmdOpen(false); if (p.id !== currentProject.id) switchProject(p.id); }}
                  data-testid={`workspace-switcher-command-item-${p.id}`}>
                  <FolderKanban size={13} className="mr-2 text-[--tx-3]" />
                  <span className="truncate">{p.name}</span>
                  {p.id === currentProject.id && <span className="ml-auto text-[9px] font-mono text-[--ai]">ACTIVE</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
};
